<template>
  <span v-if="showEmptyPlaceholder" class="has-text-grey-light">&mdash;</span>
  <div
    v-else-if="registryHit"
    v-tooltip="debugTooltip"
    class="field-resolver"
    :class="fieldStateClasses"
  >
    <!-- Single object value in view mode: wrap with DeletableItem for deleted-state styling -->
    <DeletableItem v-if="showDeletable" :value="standardProps.value">
      <component
        :is="resolvedComponent"
        v-bind="standardProps"
        :placeholder="placeholder"
        class="field-widget field-widget-appear"
        @input="(v) => $emit('edit', v)"
      />
    </DeletableItem>
    <component
      :is="resolvedComponent"
      v-else
      v-bind="standardProps"
      :placeholder="placeholder"
      class="field-widget field-widget-appear"
      @input="(v) => $emit('edit', v)"
    />
    <IconButton
      v-if="showCopyButton"
      class="copy-to-clipboard"
      icon="fa-regular fa-clipboard fa-sm"
      :tooltip="
        widgetCopyLabel ? `Copy ${widgetCopyLabel}` : 'Copy to clipboard'
      "
      size="small"
      @click="handleCopy"
    />
  </div>
  <!-- Dev-only placeholder for unresolved fields (no registry match) -->
  <span v-else-if="isDevBuild" class="dev-notice field-resolver__no-match">
    ⚠ {{ fieldMeta.name }}
    <span class="field-resolver__no-match-detail">
      (widget:
      {{
        fieldMeta?.widget?.edit?.type || fieldMeta?.widget?.view?.type || '–'
      }}, type: {{ fieldMeta?.type || '?' }})
    </span>
  </span>
</template>

<script>
/**
 * FieldResolver — the entry point for all dynamic field rendering.
 *
 * Registered globally as <FieldComponent> in main.js, so every caller in the
 * app (ItemDetails, DataTable, CreateSamples, etc.) renders fields through here.
 *
 * Resolution flow:
 *   1. Choice fields in edit mode → SelectField (dropdown); autocomplete-only
 *      text fields → AutocompleteText (text input + suggestions, issue #1260)
 *   2. `id` field in view mode → LinkedItem (TODO-SERVER-010: temporary until server sets widget)
 *   3. Try widget-based resolution: widget.type (from fieldMeta) → widgetRegistry
 *   4. Try type-based resolution: fieldMeta.type (string, number, etc.) → typeRegistry
 *   5. No match → nothing renders (unrecognized widget/type)
 *
 * Cross-cutting concerns handled here (not in individual widgets):
 *   - Empty values: shows `—` placeholder in view mode
 *   - Deleted items: wraps single object values with DeletableItem in view mode
 *   - Copy-to-clipboard: shows copy button when fieldMeta.copyable is true (view mode only)
 *
 * Array iteration is the widget's responsibility. Widgets that render per-item
 * (ItemLink, ObjectWidget) use FieldIterator internally. FieldResolver always
 * passes the full value — it never iterates arrays.
 *
 * Widgets receive ONLY the props defined in STANDARD_FIELD_PROPS (see fieldProps.js).
 * They never access $attrs, $parent, or inject. This is the hard boundary between the
 * resolver and the widget.
 */
import { computed, defineAsyncComponent, inject, unref } from 'vue'

import { isDevBuild } from '@/../app.config'
import { useCopyNotify } from '@/composables/useCopyNotify'
import { uuidv4 } from '@/utils'

import DeletableItem from './DeletableItem.vue'
import FieldSkeleton from './FieldSkeleton.vue'
import { buildStandardProps } from './interface/propBuilder'
import {
  getWidgetDefaultArgs,
  hasWidget,
  resolveType,
  resolveWidget,
} from './registry'

// SelectField is resolved directly (not via registry) because it's never
// server-driven — it's always gated by the isSelectWidget computed in setup().
// Stable module-level reference prevents re-mounting on computed re-evaluation.
const SelectFieldComponent = defineAsyncComponent({
  loader: () =>
    import('@/components/fields/widget/SelectField/detail/edit/index.vue'),
  loadingComponent: FieldSkeleton,
  delay: 0,
})
SelectFieldComponent._debugPath = 'widget/SelectField/detail/edit'

function normalizeCopyText(value) {
  if (typeof value === 'string' && value) return value
  if (typeof value === 'number') return String(value)
  return null
}

// AutocompleteText follows the same direct-resolution pattern: gated by
// isAutocompleteWidget in setup(), never server-driven by widget type.
const AutocompleteTextComponent = defineAsyncComponent({
  loader: () =>
    import('@/components/fields/widget/AutocompleteText/detail/edit/index.vue'),
  loadingComponent: FieldSkeleton,
  delay: 0,
})
AutocompleteTextComponent._debugPath = 'widget/AutocompleteText/detail/edit'

export default {
  name: 'FieldResolver',
  components: { DeletableItem },
  emits: ['edit'],
  inheritAttrs: false,
  props: {
    // --- Resolver consumes these (not forwarded to migrated widgets) ---
    // These are used to decide WHICH component to render and to build standardProps.
    // Migrated widgets never see these directly — they get the processed output instead.
    data: Object, // { value, url?, name? } — raw field data from the record
    page: { type: String, default: undefined }, // 'detail' | 'list' — override injected fieldPage
    fieldMeta: { type: Object, required: true }, // Merged model+view metadata for this field
    isEditMode: { type: Boolean, default: false }, // Whether the page is in edit mode
    bypassValidation: { type: Array, default: () => [] }, // Validation rule names to skip

    // --- Resolver uses + forwards (via buildStandardProps) ---
    fieldId: { type: String, default: '' },
    errorMessage: { type: String, default: undefined },
    hasError: { type: Boolean, default: false },
    record: { type: Object, default: null }, // Full record — used to extract cross-field dependencies

    appName: { type: String, default: '' },
    validationScope: { type: String, default: undefined },
    validationRules: { type: Array, default: () => [] },

    // --- Caller-provided display/context props (merged into widgetArgs by propBuilder) ---
    // These are declared here for camelCase normalization (Vue $attrs use kebab-case).
    // They are NOT part of STANDARD_FIELD_PROPS — widgets read them from widgetArgs.
    copyable: { type: Boolean, default: undefined },
    linkBehavior: { type: String, default: undefined },
    isElnLayout: { type: Boolean, default: false },
    placeholder: { type: String, default: undefined },
  },
  setup(props) {
    const injectedPage = inject('fieldPage', 'detail')
    const resolvedPage = computed(() => props.page || injectedPage)
    const injectedLinkBehavior = inject('linkBehavior', undefined)

    // --- Widget metadata selection ---
    const widget = computed(() => {
      if (resolvedPage.value === 'list' && props.fieldMeta?.widget?.list) {
        return props.fieldMeta.widget.list
      }
      if (props.isEditMode && props.fieldMeta?.widget?.edit) {
        return props.fieldMeta.widget.edit
      }
      if (
        props.isEditMode &&
        !editable.value &&
        props.fieldMeta?.widget?.view
      ) {
        return props.fieldMeta.widget.view
      }
      if (!props.isEditMode && props.fieldMeta?.widget?.view) {
        return props.fieldMeta.widget.view
      }
      return undefined
    })

    // --- Editable state ---
    const editable = computed(() => {
      if (!props.isEditMode) return false
      if (props.fieldMeta?.readonly) return false
      return true
    })

    // Both overrides only apply to Object widget type or fields with no widget
    // type. All other registered widgets (LinkedItem, StockLocation, etc.)
    // manage their own edit UI. SelectField loads everything with
    // page_size=max, which breaks for large collections (samples, parent
    // datasets, etc.).
    const isOverridableWidgetType = computed(() => {
      const widgetType = widget.value?.type
      return !widgetType || widgetType === 'Object'
    })

    // Autocomplete-only text fields: free text is the value, suggestions are
    // assisted typing (issue #1260). Multiple-value autocomplete fields stay
    // on SelectField — a tag list cannot be a single text input.
    const isAutocompleteWidget = computed(() => {
      if (!editable.value) return false
      if (!props.fieldMeta?.autocomplete) return false
      if (props.fieldMeta?.choices || props.fieldMeta?.multiple) return false
      return isOverridableWidgetType.value
    })

    const isSelectWidget = computed(() => {
      if (!editable.value || isAutocompleteWidget.value) return false
      if (!props.fieldMeta?.autocomplete && !props.fieldMeta?.choices)
        return false
      return isOverridableWidgetType.value
    })

    const computedFieldId = computed(() => {
      return (
        props.fieldId ||
        `${uuidv4().replace('-', '').substring(0, 8)}-${props.fieldMeta.name}`
      )
    })

    // --- Registry resolution ---
    // IMPORTANT: Do NOT include props.data here. Resolver context determines
    // WHICH component to render, not what value to pass. Including data would
    // cause resolvedComponent to re-evaluate on every keystroke, returning a
    // new function reference each time and causing Vue to remount the component
    // (losing input focus).
    // Resolver context determines WHICH component to render — pure rendering
    // context only (page, editable, isEditMode). Field-specific metadata
    // (fieldMeta) is NOT included: field-name-based overrides (id → ItemLink,
    // choices → SelectField) are handled in FieldResolver before hitting the
    // registry, keeping resolvers free of field-specific logic.
    const resolverContext = computed(() => ({
      page: resolvedPage.value,
      editable: editable.value,
      isEditMode: props.isEditMode,
    }))

    // Single source for which widget name applies in this rendering context.
    // Drives both resolvedComponent (component lookup) and widgetDefaultArgs (arg defaults)
    // so the priority order (select → id override → widget.type) is defined once.
    const resolvedWidgetName = computed(() => {
      if (isSelectWidget.value || isAutocompleteWidget.value) return null
      // TODO-SERVER-010: remove when server sets preferred_view_widget: 'LinkedItem' on `id`
      if (props.fieldMeta?.name === 'id' && !editable.value) return 'LinkedItem'
      return widget.value?.type || null
    })

    const widgetDefaultArgs = computed(() => {
      const name = resolvedWidgetName.value
      return name ? getWidgetDefaultArgs(name) : {}
    })

    const resolvedComponent = computed(() => {
      // Step 1: Choice/autocomplete fields in edit mode take priority over
      // widget/type resolution. Autocomplete-only text fields render as a
      // text input with suggestions; choice fields render as a dropdown.
      if (isAutocompleteWidget.value) return AutocompleteTextComponent
      if (isSelectWidget.value) return SelectFieldComponent
      // Step 2+3: Widget-based (name already resolved, including id → LinkedItem override)
      const name = resolvedWidgetName.value
      if (name) {
        const comp = resolveWidget(name, resolverContext.value)
        if (comp) return comp
        // Widget type is set but didn't resolve. If the widget is not registered
        // at all, render nothing — don't fall through to type-based resolution,
        // which would replace widget-specific rendering (e.g. TextArea's single-line
        // list view) with a generic type renderer.
        // If the widget IS registered but declined this context (returned null),
        // fall through to type-based resolution (e.g. AutoIncrement view mode
        // renders as a plain string).
        if (!hasWidget(name)) return null
      }
      // Step 4: Type-based (only reached when no widget.type, or widget registered but declined)
      if (props.fieldMeta?.type) {
        const comp = resolveType(props.fieldMeta.type, resolverContext.value)
        if (comp) return comp
      }
      // Step 5: No match
      return null
    })

    const registryHit = computed(() => resolvedComponent.value !== null)

    // --- Empty value handling ---
    const isValueEmpty = computed(() => {
      const val = props.data?.value
      if (val == null) return true
      if (val === 0 || val === false) return false
      if (typeof val === 'string') return val.trim() === ''
      if (Array.isArray(val)) return val.length === 0
      if (typeof val === 'object') return Object.keys(val).length === 0
      return false
    })

    const showEmptyPlaceholder = computed(() => {
      if (editable.value) return false
      if (props.isElnLayout) return false
      return isValueEmpty.value
    })

    // --- Dev-mode debug tooltip (replaces CSS overlay + title attr) ---
    const debugLabel = computed(() => {
      if (props.errorMessage) return `⚠ ${props.fieldMeta.name} (error)`
      return resolvedComponent.value?._debugPath || props.fieldMeta?.name || '?'
    })

    const debugTooltip = computed(() => {
      if (!isDevBuild) return false
      const theme =
        props.hasError || props.errorMessage ? 'debug-error' : 'debug'
      return {
        content: debugLabel.value,
        popperClass: theme,
        placement: 'top-end',
        delay: 0,
        arrow: false,
        offset: [0, 0],
      }
    })

    // --- Dev-mode field state classes ---
    const fieldStateClasses = computed(() => ({
      'field-resolver--dev': isDevBuild,
      'field-resolver--error': props.hasError || !!props.errorMessage,
      'field-resolver--list': resolvedPage.value === 'list',
      'field-resolver--edit': editable.value,
    }))

    // --- Standard props for migrated widgets ---
    const standardProps = computed(() =>
      buildStandardProps({
        data: props.data,
        fieldMeta: props.fieldMeta,
        widget: widget.value,
        record: props.record,
        fieldId: computedFieldId.value,
        editable: editable.value,
        bypassValidation: props.bypassValidation,
        validationScope: props.validationScope,
        callerValidationRules: props.validationRules,
        errorMessage: props.errorMessage,
        hasError: props.hasError,
        copyable: props.copyable,
        linkBehavior: props.linkBehavior ?? unref(injectedLinkBehavior),
        isElnLayout: props.isElnLayout,
        appName: props.appName,
        page: resolvedPage.value,
        widgetDefaultArgs: widgetDefaultArgs.value,
      }),
    )

    // --- Deleted state wrapping ---
    // In view mode, single object values may have { deleted: true }.
    // DeletableItem shows strikethrough + tooltip for deleted items.
    // For arrays, widgets handle DeletableItem per-item via FieldIterator.
    // Skip in edit mode — edit widgets manage their own UI.
    const showDeletable = computed(() => {
      if (editable.value) return false
      const val = standardProps.value.value
      return val != null && typeof val === 'object' && !Array.isArray(val)
    })

    // --- Copy-to-clipboard ---
    // Two sources: fieldMeta.copyable (string fields) and widgetArgs.copyField (object fields).
    // widgetArgs merges: registration defaults (e.g. LinkedItem declares copyField:'id') →
    // UI overrides → server widget.arguments (server wins). copyable=false suppresses the button.
    const { copyWithNotify } = useCopyNotify()

    const widgetCopyField = computed(
      () => standardProps.value.widgetArgs?.copyField,
    )
    const widgetCopyLabel = computed(
      () => standardProps.value.widgetArgs?.copyLabel,
    )

    const copyText = computed(() => {
      const val = props.data?.value
      if (widgetCopyField.value) {
        // Object value (e.g. LinkedItem reference): extract the named subproperty.
        // String value (e.g. the `id` field itself): the field IS the target — copy directly.
        if (typeof val === 'object')
          return normalizeCopyText(val?.[widgetCopyField.value])
        return normalizeCopyText(val)
      }
      if (props.fieldMeta?.copyable) return normalizeCopyText(val)
      return null
    })

    const showCopyButton = computed(() => {
      if (editable.value) return false
      if (standardProps.value.widgetArgs?.copyable === false) return false
      return copyText.value !== null
    })

    const handleCopy = () => {
      copyWithNotify(copyText.value, widgetCopyLabel.value)
    }

    return {
      widget,
      editable,
      computedFieldId,
      resolvedComponent,
      registryHit,
      isValueEmpty,
      showEmptyPlaceholder,
      showDeletable,
      copyText,
      widgetCopyLabel,
      showCopyButton,
      handleCopy,
      debugLabel,
      debugTooltip,
      fieldStateClasses,
      standardProps,
      isDevBuild,
      resolvedPage,
    }
  },
}
</script>

<style lang="scss" scoped>
// View mode (detail): inline-flex keeps the copy button on the same line as the value.
// max-width:100% prevents the resolver from overflowing its parent; the field-widget
// (flex item with flex-shrink:1 + min-width:0) truncates long text with ellipsis.
.field-resolver:not(.field-resolver--list):not(.field-resolver--edit) {
  display: inline-flex;
  align-items: baseline;
  min-width: 0;
  max-width: 100%;
}
// Edit mode: stretch to full width so inputs/selects fill the available space.
.field-resolver--edit:not(.field-resolver--list) {
  display: flex;
  align-items: baseline;
  min-width: 0;
  width: 100%;
}
.field-resolver--list {
  display: inline-flex;
  align-items: center;
  vertical-align: middle;
}
.field-resolver--dev {
  position: relative;
}
// Show outline + debug label together on hover / focus-within
.field-resolver--dev:hover,
.field-resolver--dev:focus-within {
  outline: 1px dashed var(--success);
}
// In list/table mode, parent .table-cell has overflow:hidden which clips
// CSS outlines. Use an inset box-shadow instead — renders inside the element.
.field-resolver--dev.field-resolver--list {
  outline: none;
}
.field-resolver--dev.field-resolver--list:hover,
.field-resolver--dev.field-resolver--list:focus-within {
  outline: none;
  box-shadow: inset 0 0 0 1px
    color-mix(in srgb, var(--success) 40%, transparent);
}
.field-resolver--dev.field-resolver--error:hover,
.field-resolver--dev.field-resolver--error:focus-within {
  outline: 1px dotted var(--danger);
  outline-offset: 2px;
  border-radius: 3px;
}
.field-resolver--dev.field-resolver--warning:hover,
.field-resolver--dev.field-resolver--warning:focus-within {
  outline: 1px dotted var(--warning);
  outline-offset: 2px;
  border-radius: 3px;
}

// Dev-only placeholder for fields with no registry match
.field-resolver__no-match {
  display: inline-block;
  padding: 2px 8px;
  font-size: 11px;
}
.field-resolver__no-match-detail {
  color: var(--text-hint);
  font-size: 10px;
}
</style>

<!-- Unscoped: .field-widget-appear is merged onto child component root elements -->
<style lang="scss">
// View mode (detail + list): allow the widget to shrink so the copy button stays inline.
.field-resolver:not(.field-resolver--edit) > .field-widget {
  min-width: 0;
  flex-shrink: 1;
}
// Edit mode: stretch widget to fill the resolver so inputs/selects span the row.
.field-resolver--edit:not(.field-resolver--list) > .field-widget {
  width: 100%;
}

.field-widget-appear {
  animation: field-widget-fade-in 0.3s ease-out;
}

@keyframes field-widget-fade-in {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}
</style>
